import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { JsonService } from '../_services/json.service';
import { LoadingService } from '../_services/loading.service';
import { LoggerService } from '../_services/logger.service';
import { GlobalService } from '../_services/global.service';
import { TranslateService } from '@ngx-translate/core';
import { UserPerformanceReview } from '../_models/user-performance-review.model';
import { Group } from '../_models';

@Component({
  selector: 'app-user-performance-reviews',
  templateUrl: './user-performance-reviews.component.html',
  styleUrls: ['./user-performance-reviews.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserPerformanceReviewsComponent implements OnInit, OnDestroy {
  performanceData: UserPerformanceReview[] = [];
  filteredData: UserPerformanceReview[] = [];
  totais = this.totaisZerados();

  listaUsuarios: any[] = [];
  listaPaises: any[] = [];
  listGrupos: Group[] = [];

  filtro: any = {
    usuario: '',
    pais: '',
    ano: '',
    mes: '',
    dataInicial: '',
    dataFinal: '',
    groupId: ''
  };

  readonly listAnos: string[] = (() => {
    const base = new Date().getFullYear();
    return [base - 1, base, base + 1].map(y => String(y));
  })();

  readonly listMeses = [
    { label: 'Janeiro',   value: '01' },
    { label: 'Fevereiro', value: '02' },
    { label: 'Março',     value: '03' },
    { label: 'Abril',     value: '04' },
    { label: 'Maio',      value: '05' },
    { label: 'Junho',     value: '06' },
    { label: 'Julho',     value: '07' },
    { label: 'Agosto',    value: '08' },
    { label: 'Setembro',  value: '09' },
    { label: 'Outubro',   value: '10' },
    { label: 'Novembro',  value: '11' },
    { label: 'Dezembro',  value: '12' },
  ];

  filtroError: string = '';

  public isAdmin = false;
  public isAdminGrupo = false;
  public isAdminGlobalGrupo = false;

  private destroy$ = new Subject<void>();

  constructor(
    private jsonService: JsonService,
    private loadingService: LoadingService,
    private logger: LoggerService,
    private globalService: GlobalService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    const now = new Date();
    this.filtro.ano = String(now.getFullYear());
    this.filtro.mes = String(now.getMonth() + 1).padStart(2, '0');
    this.isAdmin = this.globalService.isAdmin();
    this.isAdminGrupo = this.globalService.isAdminGrupo();
    this.isAdminGlobalGrupo = this.globalService.isAdminGlobalGrupo();
    this.loadCombosList();
    this.loadCountries();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackByItem(_: number, item: UserPerformanceReview): string {
    return `${item.emailRevisor}_${item.dataRevisao}`;
  }

  private periodoToAaaamm(): string {
    return `${this.filtro.ano}${this.filtro.mes}`;
  }

  formatarDataInput(event: Event, campo: 'dataInicial' | 'dataFinal'): void {
    const input = event.target as HTMLInputElement;
    const digitos = input.value.replace(/\D/g, '').slice(0, 8);

    let formatado = digitos;
    if (digitos.length > 4)      formatado = `${digitos.slice(0, 2)}/${digitos.slice(2, 4)}/${digitos.slice(4)}`;
    else if (digitos.length > 2) formatado = `${digitos.slice(0, 2)}/${digitos.slice(2)}`;

    this.filtro[campo] = formatado;
    input.value = formatado;
  }

  private dataBrParaIso(dataBr: string): string | null {
    const m = (dataBr || '').match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return null;
    const [, dd, mm, yyyy] = m;
    return `${yyyy}-${mm}-${dd}`;
  }

  private loadCombosList(): void {
    const onlyManaged = this.isAdminGrupo && !this.isAdmin && !this.isAdminGlobalGrupo;
    this.jsonService.listGroupUsers(onlyManaged)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: resp => {
          this.listaUsuarios = (resp || []).sort((a: any, b: any) => (a.email || '').localeCompare(b.email || ''));
          this.cdr.markForCheck();
        },
        error: erro => { this.logger.error('Erro ao carregar usuários', erro); }
      });
  }

  private loadCountries(): void {
    this.jsonService.listCountry()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: resp => {
          this.listaPaises = resp.sort((a: any, b: any) => a.name.localeCompare(b.name));
          this.cdr.markForCheck();
        },
        error: erro => { this.logger.error('Erro ao carregar países', erro); }
      });
  }

  onPaisChange(pais: string): void {
    this.filtro.pais = pais;
    this.filtro.groupId = '';
    this.filtro.usuario = '';
    this.loadCombosList();
    if (!this.filtro.pais) {
      this.listGrupos = [];
      this.cdr.markForCheck();
      return;
    }
    const paisSelecionado = this.listaPaises.find((p: any) => p.id === this.filtro.pais);
    if (!paisSelecionado?.countryCode) {
      this.listGrupos = [];
      this.cdr.markForCheck();
      return;
    }
    this.jsonService.listGroupsByCountry(paisSelecionado.countryCode)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: grupos => { this.listGrupos = grupos || []; this.cdr.markForCheck(); },
        error: erro => { this.logger.error('Erro ao carregar grupos', erro); this.listGrupos = []; this.cdr.markForCheck(); }
      });
  }

  onGrupoChange(groupId: string): void {
    this.filtro.groupId = groupId;
    this.filtro.usuario = '';
    if (!this.filtro.groupId) {
      this.loadCombosList();
      return;
    }
    this.jsonService.getGroupListUsers(this.filtro.groupId, this.filtro.pais)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: resp => {
          this.listaUsuarios = (resp || []).sort((a: any, b: any) => (a.email || '').localeCompare(b.email || ''));
          this.cdr.markForCheck();
        },
        error: erro => {
          this.logger.error('Erro ao carregar usuários do grupo', erro);
          this.listaUsuarios = [];
          this.cdr.markForCheck();
        }
      });
  }

  loadData(): void {
    const aaaamm = this.periodoToAaaamm();

    if (!this.filtro.pais) {
      this.filtroError = this.translate.instant('Selecione o país para realizar a pesquisa.');
      this.loadingService.hide();
      this.cdr.markForCheck();
      return;
    }
    if (!aaaamm || aaaamm.length !== 6) {
      this.filtroError = this.translate.instant('Selecione o período para realizar a pesquisa.');
      this.loadingService.hide();
      this.cdr.markForCheck();
      return;
    }
    this.filtroError = '';

    let params = new HttpParams()
      .append('countryId', this.filtro.pais)
      .append('periodo', aaaamm);

    if (this.filtro.usuario) {
      params = params.append('userEmail', this.filtro.usuario);
    }
    const dataInicialIso = this.dataBrParaIso(this.filtro.dataInicial);
    const dataFinalIso = this.dataBrParaIso(this.filtro.dataFinal);
    if (dataInicialIso) {
      params = params.append('startDate', dataInicialIso);
    }
    if (dataFinalIso) {
      params = params.append('endDate', dataFinalIso);
    }
    if (this.filtro.groupId) {
      params = params.append('groupId', this.filtro.groupId);
    }

    this.jsonService.getUserPerformanceTracking(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: resp => {
          this.performanceData = resp || [];
          this.filteredData = this.performanceData;
          this.calcularTotais();
          this.loadingService.hide();
          this.cdr.markForCheck();
        },
        error: err => {
          this.logger.error('Erro ao carregar dados de performance', err);
          this.loadingService.hide();
          this.cdr.markForCheck();
        }
      });
  }

  atualizaFiltro(): void {
    this.loadingService.show();
    this.loadData();
  }

  exportToExcel(): void {
    if (!this.filteredData || this.filteredData.length === 0) {
      alert(this.translate.instant('Sem dados para exportar'));
      return;
    }

    const csv = this.convertToCSV(this.filteredData);
    const aaaamm = this.periodoToAaaamm();
    const countryCode = (this.listaPaises.find((p: any) => p.id === this.filtro.pais) as any)?.countryCode || '';
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    const ts = new Date(); const stamp = `${ts.getFullYear()}${String(ts.getMonth()+1).padStart(2,'0')}${String(ts.getDate()).padStart(2,'0')}${String(ts.getHours()).padStart(2,'0')}${String(ts.getMinutes()).padStart(2,'0')}${String(ts.getSeconds()).padStart(2,'0')}`;
    link.setAttribute('download', `${aaaamm}–${countryCode}–performance-revisor_${stamp}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  clearFiltros(): void {
    const now = new Date();
    this.filtro = {
      usuario: '',
      pais: '',
      ano: String(now.getFullYear()),
      mes: String(now.getMonth() + 1).padStart(2, '0'),
      dataInicial: '',
      dataFinal: '',
      groupId: ''
    };
    this.listGrupos = [];
    this.performanceData = [];
    this.filteredData = [];
    this.totais = this.totaisZerados();
    this.filtroError = '';
    this.loadCombosList();
    this.cdr.markForCheck();
  }

  private calcularTotais(): void {
    const sum = (campo: keyof UserPerformanceReview) =>
      this.filteredData.reduce((acc, r) => acc + (Number(r[campo]) || 0), 0);

    this.totais = {
      totalReceitas:   sum('totalReceitas'),
      impresas:        sum('impresas'),
      manuscritas:     sum('manuscritas'),
      totalPxs:        sum('totalPxs'),
      totalProductos:  sum('totalProductos'),
      totalInteracoes:  sum('totalInteracoes')
    };
  }

  private totaisZerados() {
    return { totalReceitas: 0, impresas: 0, manuscritas: 0, totalPxs: 0, totalProductos: 0, totalInteracoes: 0 };
  }

  private convertToCSV(data: UserPerformanceReview[]): string {
    const headers = [
      this.translate.instant('País'),
      this.translate.instant('Grupo'),
      this.translate.instant('Usuário'),
      this.translate.instant('Mês'),
      this.translate.instant('Data'),
      this.translate.instant('Total Receitas'),
      this.translate.instant('Impresas'),
      this.translate.instant('Manuscritas'),
      this.translate.instant('Total Pxs'),
      this.translate.instant('Total Interacoes.'),
      this.translate.instant('Hora Início'),
      this.translate.instant('Hora Fim'),
      this.translate.instant('Tempo Neto'),
      this.translate.instant('Tempo x Rxs'),
      this.translate.instant('Tempo Transición'),
      this.translate.instant('Prom Rxs/hora')
    ];

    const rows = data.map(item => [
      item.nomePais,
      item.grupo,
      item.emailRevisor,
      item.mes,
      item.dataRevisao,
      item.totalReceitas,
      item.impresas,
      item.manuscritas,
      item.totalPxs,
      item.totalInteracoes,
      item.horaInicio,
      item.horaFim,
      item.tempoNeto,
      item.tempoXRxs,
      item.tempoTransicao,
      item.promRxsHora
    ]);

    return [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${cell ?? ''}"`).join(';'))
    ].join('\n');
  }
}
